import { useEffect, useRef, useState } from 'react';
import Modal from '../../components/Modal';
import Select from '../../components/Select';
import Button from '../../components/Button';
import ErrorMessages from '../../components/ErrorMessages.js';
import parkingAPI from '../../api/parking';
import formatPin from '../../helpers/formatPin';
import requsetErrorMessage from '../../helpers/requsetErrorMessage';
import useLockBodyScroll from '../../customHooks/useLockBodyScroll';

// Assign a free (Verfügbar) parking pass to THIS fan (owner), started from the
// fan detail page. Admin picks a field, then one of its unassigned passes.
const AssignPassToFanModal = ({ fanId, seasonId, onClose, onAssigned }) => {
    const modalRef = useRef(null);
    useLockBodyScroll();
    const [fields, setFields] = useState([]);
    const [fieldId, setFieldId] = useState(null);
    const [passes, setPasses] = useState([]);
    const [loadingPasses, setLoadingPasses] = useState(false);
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        parkingAPI
            .getFields()
            .then((res) => setFields(res.data || []))
            .catch(() => setFields([]));
    }, []);

    useEffect(() => {
        if (!fieldId) {
            setPasses([]);
            return;
        }
        const load = async () => {
            setLoadingPasses(true);
            setSelected(null);
            try {
                const params = { parking_field_id: fieldId, available: true };
                if (seasonId) params.season_id = seasonId;
                const res = await parkingAPI.getPasses({ params });
                setPasses(res.data || []);
            } catch (e) {
                setPasses([]);
            }
            setLoadingPasses(false);
        };
        load();
    }, [fieldId, seasonId]);

    const doAssign = async () => {
        if (!selected) return;
        setSaving(true);
        setError(null);
        try {
            await parkingAPI.assignOwner(selected.value, { user_id: fanId });
            onAssigned && onAssigned();
        } catch (err) {
            setError(requsetErrorMessage(err && err.message ? err.message : err));
            setSaving(false);
        }
    };

    const close = () => onClose && onClose();

    const fieldOptions = fields.map((f) => ({ value: f.id, label: f.name }));
    const passOptions = passes.map((p) => ({
        value: p.id,
        label: `${formatPin(p.assign_token)} · #${p.id}${
            p.type === 'full_season' ? ' · Saison' : ' · Einzelspiel'
        }`,
    }));

    return (
        <Modal title="Parkkarte zuteilen" ref={modalRef} onClose={close} maxWidth="480px" disabled={saving}>
            <p style={{ color: '#333', marginBottom: '10px' }}>Parkplatz:</p>
            <Select
                options={fieldOptions}
                onChangeCallback={(e) => setFieldId(e ? e.value : null)}
                isClearable
                hasError={false}
                placeholder="Parkplatz wählen…"
                noOptionsMsg="Keine Parkplätze"
            />

            <p style={{ color: '#333', margin: '15px 0 10px' }}>Verfügbare Parkkarte:</p>
            <Select
                options={passOptions}
                onChangeCallback={(e) => setSelected(e)}
                isLoading={loadingPasses}
                isClearable
                hasError={false}
                disabled={!fieldId}
                placeholder={fieldId ? 'Parkkarte wählen…' : 'Zuerst Parkplatz wählen'}
                noOptionsMsg={fieldId ? 'Keine verfügbaren Parkkarten' : ''}
            />

            <div style={{ marginTop: '15px' }}>
                <ErrorMessages messages={error || ''} />
            </div>

            <div className="Modal-Controls" style={{ marginTop: '20px' }}>
                <Button
                    label="Zuteilen"
                    loading={saving}
                    disabled={saving || !selected}
                    onClick={doAssign}
                    style={{ width: '120px' }}
                />
                <Button
                    label="Abbrechen"
                    theme="light"
                    disabled={saving}
                    onClick={close}
                    style={{ width: '120px', marginLeft: '15px' }}
                />
            </div>
        </Modal>
    );
};

export default AssignPassToFanModal;
